import React from 'react'
import { Box, Fade, Paper, Tooltip, Typography, useTheme, alpha } from '@mui/material'
import type { Theme } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import StarIcon from '@mui/icons-material/Star'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { TrackerStep, TrackerNote, TrackerStepInfo } from '../../types/gotypes'

// Shared content column width so the header, KPIs, tabs and content all align
// in one centred column (with balanced margins on wide monitors).
export const TRACKER_MAX_WIDTH = 1280

// StepKey explains what each workflow step means, in visible text (not just a
// hover tooltip — those don't show on screenshots or read at a glance).
export const StepKey: React.FC<{ legend: TrackerStepInfo[]; accent: string }> = ({ legend, accent }) => {
  const theme = useTheme()
  if (!legend || legend.length === 0) return null
  return (
    <Paper
      elevation={0}
      sx={{ p: 2, mb: 2.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.text.primary, 0.015) }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: theme.palette.text.secondary, mb: 1.5 }}>
        HOW THE STEPS WORK
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        {legend.map((step, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.25 }}>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 800,
                color: accent,
                bgcolor: alpha(accent, 0.12),
              }}
            >
              {i + 1}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{step.name}</Typography>
              {step.description && (
                <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary, lineHeight: 1.45 }}>
                  {step.description}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  )
}
import { useCountUp, useMounted, usePrefersReducedMotion } from './motion'

// Reveal staggers children into view on mount (instant when reduced motion).
export const Reveal: React.FC<{ index?: number; children: React.ReactNode }> = ({ index = 0, children }) => {
  const reduced = usePrefersReducedMotion()
  if (reduced) return <>{children}</>
  return (
    <Fade in timeout={500} style={{ transitionDelay: `${Math.min(index, 8) * 70}ms` }}>
      <Box
        sx={{
          '@keyframes trackerRise': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          animation: `trackerRise 0.5s cubic-bezier(0.22,1,0.36,1) ${Math.min(index, 8) * 70}ms both`,
        }}
      >
        {children}
      </Box>
    </Fade>
  )
}

// ---- Status colours ------------------------------------------------------

export const statusColor = (status: string, theme: Theme): string => {
  switch (status) {
    case 'Accepted':
    case 'Live':
      return theme.palette.success.main
    case 'UAT Issued':
      return theme.palette.warning.main
    case 'In Progress':
      return theme.palette.info.main
    default:
      return theme.palette.text.disabled
  }
}

// NoteLine shows the latest status note (most recent GitHub comment) for an item.
export const NoteLine: React.FC<{ note?: TrackerNote }> = ({ note }) => {
  const theme = useTheme()
  if (!note || !note.text) return null
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        mt: 1.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.info.main, 0.06),
        border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
      }}
    >
      <ChatBubbleOutlineIcon sx={{ fontSize: 15, color: theme.palette.info.main, mt: '2px', flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, color: theme.palette.info.main, mb: 0.25 }}>
          LATEST UPDATE
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: theme.palette.text.primary, lineHeight: 1.45 }}>{note.text}</Typography>
        <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary, mt: 0.25 }}>
          {note.author}
          {note.date ? ` · ${note.date}` : ''}
        </Typography>
      </Box>
    </Box>
  )
}

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const theme = useTheme()
  const color = statusColor(status, theme)
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.25,
        py: 0.25,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.3,
        color,
        bgcolor: alpha(color, 0.12),
        border: `1px solid ${alpha(color, 0.35)}`,
        whiteSpace: 'nowrap',
      }}
    >
      {status || 'Pending'}
    </Box>
  )
}

// ---- Circular progress ring (KPI cards) ----------------------------------

interface ProgressRingProps {
  pct: number
  value: number
  total?: number
  unit?: string
  color: string
  size?: number
}

// ProgressRing draws an SVG ring that fills from zero on mount while the centre
// number counts up. At 100% it earns a soft celebratory glow.
export const ProgressRing: React.FC<ProgressRingProps> = ({ pct, value, total, unit, color, size = 116 }) => {
  const theme = useTheme()
  const reduced = usePrefersReducedMotion()
  const mounted = useMounted()
  const stroke = 9
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const clamped = Math.max(0, Math.min(100, pct))
  // Grow from empty on first paint, then ease to the real value.
  const shown = reduced ? clamped : mounted ? clamped : 0
  const offset = c - (shown / 100) * c

  const animValue = useCountUp(value, !reduced)
  const display =
    total != null ? `${Math.round(animValue)}/${total}` : `${Math.round(animValue)}${unit || ''}`
  const complete = clamped >= 100

  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={alpha(theme.palette.text.primary, 0.08)} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{
            transition: reduced ? 'none' : 'stroke-dashoffset 1.1s cubic-bezier(0.22,1,0.36,1)',
            filter: complete ? `drop-shadow(0 0 6px ${alpha(color, 0.6)})` : 'none',
          }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1, color: complete ? color : theme.palette.text.primary }}>
          {display}
        </Typography>
      </Box>
    </Box>
  )
}

// ---- Stream A 4-step progress rail ---------------------------------------

const stepHint = (state: string): string => {
  switch (state) {
    case 'complete':
      return 'Complete'
    case 'active':
      return 'In progress'
    case 'live':
      return 'Live'
    default:
      return 'Pending'
  }
}

export const StepRail: React.FC<{ steps: TrackerStep[]; accentColor: string }> = ({ steps, accentColor }) => {
  const theme = useTheme()

  const dotColor = (state: string): string => {
    switch (state) {
      case 'complete':
        return theme.palette.success.main
      case 'active':
        return theme.palette.info.main
      case 'live':
        return accentColor
      default:
        return alpha(theme.palette.text.primary, 0.22)
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {steps.map((step, i) => {
        const color = dotColor(step.state)
        const filled = step.state !== 'pending'
        return (
          <React.Fragment key={step.index}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', width: 86 }}>
              <Tooltip arrow title={`${step.name} — ${stepHint(step.state)}`}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: filled ? color : 'transparent',
                    border: `2px solid ${color}`,
                    color: '#fff',
                    transition: 'all 0.3s ease',
                    ...(step.state === 'active' && {
                      animation: 'trackerPulse 1.8s ease-in-out infinite',
                      '@keyframes trackerPulse': {
                        '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(color, 0.5)}` },
                        '50%': { boxShadow: `0 0 0 7px ${alpha(color, 0)}` },
                      },
                    }),
                  }}
                >
                  {step.state === 'complete' && <CheckIcon sx={{ fontSize: 18 }} />}
                  {step.state === 'live' && <StarIcon sx={{ fontSize: 18 }} />}
                </Box>
              </Tooltip>
              <Typography
                sx={{
                  mt: 1,
                  fontSize: 10.5,
                  lineHeight: 1.3,
                  textAlign: 'center',
                  fontWeight: filled ? 700 : 500,
                  color: filled ? theme.palette.text.primary : theme.palette.text.secondary,
                }}
              >
                {step.name}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  mt: '14px',
                  borderRadius: 1,
                  bgcolor:
                    steps[i + 1].state !== 'pending' || step.state === 'complete' || step.state === 'live'
                      ? theme.palette.success.main
                      : alpha(theme.palette.text.primary, 0.15),
                  transition: 'background-color 0.3s ease',
                }}
              />
            )}
          </React.Fragment>
        )
      })}
    </Box>
  )
}
